const EventNames = require('@/lib/Events/EventNames');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const OnlineTaskType = require('@/lib/OnlineQueue/OnlineTaskType');
const TradingSession = require('@/lib/TradingSessions/TradingSession');
const TradingSessionCollection = require('@/lib/TradingSessions/TradingSessionCollection');
const TradingSessionEvent = require('@/lib/TradingSessions/TradingSessionEvent');
const TradingSessionEventType = require('@/lib/TradingSessions/TradingSessionEventType');
const { apiResponseToError } = require('@/lib/Api');
const { hydrate } = require('@/lib/Hydration');

/**
 * Note this cannot have DeviceManager as a dependency as it would cause a circular dependency.
 *
 * @constructor
 *
 * @param {Hydrator} hydrator
 * @param {CurrentDevice} currentDevice
 * @param {EventBus} eventBus
 * @param {OnlineQueue} onlineQueue
 * @param tradingSessionResource
 * @param {UserService} userService
 */
const TradingSessionManager = function (
    hydrator,
    currentDevice,
    eventBus,
    onlineQueue,
    tradingSessionResource,
    userService
) {
    /**
     * @type {Hydrator}
     */
    this.hydrator = hydrator;

    /**
     * @type {CurrentDevice}
     */
    this.currentDevice = currentDevice;

    /**
     * @type {EventBus}
     */
    this.eventBus = eventBus;

    /**
     * @type {OnlineQueue}
     */
    this.onlineQueue = onlineQueue;

    this.tradingSessionResource = tradingSessionResource;

    /**
     * @type {UserService}
     */
    this.userService = userService;

    this.log = logFactory('TradingSessionManager');
};

TradingSessionManager.prototype = {
    /**
     * Add an event to the trading session event log.
     *
     * @param {TradingSessionEvent} tradingSessionEvent
     * @param {boolean=false} [skippable]
     *
     * @return {Promise<OnlineTask>}
     */
    async saveEvent(tradingSessionEvent, skippable = false) {
        // If the event has no operatorID use the current user's userID if possible.
        if (!tradingSessionEvent.operatorID) {
            if (this.userService.getUserID()) {
                tradingSessionEvent.setOperatorID(this.userService.getUserID());
            } else {
                const err = 'Cannot save ' + tradingSessionEvent.action + ' trading session event because it does not contain an operatorID.';
                this.log.error(
                    err,
                    tradingSessionEvent
                );
                return Promise.reject(new Error(err));
            }
        }

        // If the event has no tradingSessionID use the current device's tradingSessionID if possible.
        if (!tradingSessionEvent.tradingSessionID) {
            if (this.currentDevice.getTradingSessionID()) {
                tradingSessionEvent.setTradingSessionID(this.currentDevice.getTradingSessionID());
            } else {
                const err = 'Cannot save ' + tradingSessionEvent.action + ' trading session event because it does not contain a tradingSessionID.';
                this.log.error(
                    err,
                    tradingSessionEvent
                );
                return Promise.reject(new Error(err));
            }
        }

        // Convert the TradingSessionEvent to an object of data to send to the API.
        const tradingSessionEventData = tradingSessionEvent.toApiData();

        const task = this.onlineQueue.createTask(
            OnlineTaskType.SAVE_TRADING_SESSION_ENTRY,
            tradingSessionEventData,
            `Create ${tradingSessionEvent.action} trading session entry.`
        );

        task.skippable = skippable;
        this.log.log(
            tradingSessionEvent.action + ' trading session task created.',
            task
        );

        const result = await this.onlineQueue.addTask(task);

        this.eventBus.broadcast(
            EventNames.TRADING_SESSION_EVENT_CREATED,
            { data: tradingSessionEventData }
        );

        return result;
    },

    /**
     * Get the currently active trading session for the device from the API.
     * Callback receives either the TradingSession model,
     * false if there is no open session for the device,
     * or null if there was an error.
     *
     * @param {Device} device
     *
     * @return {Promise<TradingSession|null>}
     */
    getCurrentSession: function getCurrentSession(device) {
        return new Promise((resolve, reject) => {
            this.tradingSessionResource.query(
                {
                    deviceID: device.ID,
                    trading: 1,
                    resolve: 'device',
                },
                (response) => {
                    if (response.length > 0) {
                        let tradingSession = this.hydrator.hydrate(
                            response[0],
                            new TradingSession()
                        );
                        resolve(tradingSession);
                    } else {
                        resolve(false);
                    }
                },
                (err) => reject(apiResponseToError(err))
            );
        });
    },

    /**
     * @param {number} deviceID
     *
     * @return {Promise<TradingSessionCollection>}
     */
    getSessionsForDevice(deviceID) {
        return new Promise((resolve, reject) => {
            this.tradingSessionResource.query(
                {
                    deviceID,
                    orderBy: 'startedAt',
                    orderDirection: 'DESC',
                    limit: 100,
                    resolve: 'device',
                },
                (response) => {
                    resolve(
                        new TradingSessionCollection(
                            this.hydrator.hydrateArray(
                                response,
                                () => new TradingSession()
                            )
                        )
                    );
                },
                () => {
                    reject();
                }
            );
        });
    },

    /**
     * @return {Promise<TradingSessionCollection>}
     */
    getSessions() {
        return new Promise((resolve, reject) => {
            this.tradingSessionResource.query(
                {
                    orderBy: 'startedAt',
                    orderDirection: 'DESC',
                    limit: 300,
                    resolve: 'device',
                },
                (response) => {
                    resolve(
                        new TradingSessionCollection(
                            this.hydrator.hydrateArray(
                                response,
                                () => new TradingSession()
                            )
                        )
                    );
                },
                () => {
                    reject();
                }
            );
        });
    },

    /**
     * @param {number} id
     *
     * @returns {Promise<TradingSession>}
     */
    getSession(id) {
        return new Promise((resolve, reject) => {
            this.tradingSessionResource.get(
                {
                    id,
                    resolve: 'device',
                },
                (response) => {
                    resolve(hydrate(response, new TradingSession()));
                },
                () => {
                    reject();
                }
            );
        });
    },

    /**
     * Check the API for an existing open trading session.
     * If one was found send a resume event.
     * If one was not found open one.
     *
     * @param {Device} device
     *
     * @return {Promise<TradingSession>}
     */
    async resumeOrOpenSession(device) {
        // Try asking the API for the current trading session for this device.
        // This will throw an error if the request fails, but will resolve with null if the request succeeds and there
        // is no current trading session.
        let tradingSession = await this.getCurrentSession(device);

        if (tradingSession && tradingSession.ID) {
            // Found an existing open trading session.
            this.log.debug(`Existing trading session found for for device ${device.ID}:`, tradingSession.ID);

            this.saveEvent(
                new TradingSessionEvent(TradingSessionEventType.DEVICE_RESUME)
            );

            return tradingSession;
        }

        // No existing session found - start a new one.
        this.log.debug(`No existing trading session found for device ${device.ID}.`);

        tradingSession = await this.openSession(
            device
        );
        this.log.log(`Started a new trading session for device ${device.ID}:`, tradingSession.ID);

        // Add default float.
        this.saveEvent(
            (new TradingSessionEvent(TradingSessionEventType.CASH_FLOAT))
                .setTradingSessionID(tradingSession.ID)
                .setOperatorID(this.userService.getUserID())
                .setAmount(device.defaultFloat)
        )
            .then(() => {})
            .catch(() => {});

        return tradingSession;
    },

    /**
     * Open a trading session.
     *
     * @param {Device} device
     *
     * @return {Promise<TradingSession>}
     */
    openSession: function openSession(
        device,
    ) {
        return new Promise((resolve, reject) => {
            if (!device || !device.ID) {
                throw new Error('A device must be selected before starting a trading session.');
            }

            // Ask API to start a new trading session.
            // If one already exists it will return a 409 status and the existing session.
            this.tradingSessionResource.save(
                {
                    operatorID: this.userService.getUserID(),
                    deviceID: device.ID,
                    resolve: 'device',
                },
                (response) => {
                    let tradingSession = this.hydrator.hydrate(
                        response,
                        new TradingSession()
                    );

                    resolve(tradingSession);
                },
                (err) => reject(apiResponseToError(err))
            );
        });
    },

    /**
     * Close a trading session.
     * May return false if there is an error, e.g. if the session is already closed.
     *
     * @param {TradingSession} tradingSession
     */
    closeSession: function closeSession(tradingSession) {
        let self = this;

        return new Promise((resolve, reject) => {
            this.tradingSessionResource.remove(
                {
                    id: tradingSession.ID,
                    resolve: 'device',
                },
                function (response) {
                    // Response contains the updated trading session object.
                    tradingSession = self.hydrator.hydrate(
                        response,
                        new TradingSession()
                    );

                    resolve(tradingSession);
                },
                (err) => reject(apiResponseToError(err))
            );
        });
    },

    /**
     * Get a trading session summary  from the API.
     *
     * @param {TradingSession} tradingSession
     */
    getSummary: function getSummary(tradingSession) {
        return new Promise((resolve, reject) => {
            this.tradingSessionResource.getSummary(
                {
                    id: tradingSession.ID,
                },
                function (response) {
                    resolve(response);
                },
                function () {
                    reject();
                }
            );
        });
    },
};

module.exports = TradingSessionManager;
